import React from "react";
import { CheckCircle2, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const _en = () => { try { return localStorage.getItem("athlynx_lang") === "en"; } catch { return false; } };

export const SCard = ({ title, desc, children, className, testid, right }) => (
  <section className={cn("rounded-2xl border border-white/[0.07] bg-surface p-5 sm:p-6", className)} data-testid={testid}>
    {(title || right) && (
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          {title && <h3 className="text-sm font-semibold">{title}</h3>}
          {desc && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </section>
);

export const SRow = ({ label, value, badge, testid }) => (
  <div className="flex items-center justify-between gap-4 py-2.5 border-b border-white/[0.05] last:border-0" data-testid={testid}>
    <span className="text-sm text-muted-foreground shrink-0">{label}</span>
    <span className="text-sm font-medium text-right flex items-center gap-2 min-w-0">
      <span className="truncate">{value ?? "—"}</span>
      {badge}
    </span>
  </div>
);

export const VerifiedBadge = ({ verified, label }) => (
  <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] shrink-0",
    verified ? "text-green-500 border-green-500/25 bg-green-500/[0.06]" : "text-muted-foreground border-white/[0.1]")}>
    {verified && <CheckCircle2 className="h-3 w-3" />}
    {label || (verified ? (_en() ? "Verified" : "Vérifié") : (_en() ? "Not verified" : "Non vérifié"))}
  </span>
);

export const SectionShell = ({ title, desc, children, testid }) => (
  <div className="space-y-4 min-w-0" data-testid={testid}>
    <Link to="/settings" className="md:hidden inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="settings-back">
      <ChevronLeft className="h-4 w-4" /> {_en() ? "Settings" : "Paramètres"}
    </Link>
    <div>
      <h2 className="text-xl font-semibold tracking-[-0.01em]">{title}</h2>
      {desc && <p className="text-sm text-muted-foreground mt-1">{desc}</p>}
    </div>
    {children}
  </div>
);

export const fmtDateFr = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(_en() ? "en-US" : "fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

export const relativeFrTime = (iso) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const en = _en();
  if (m < 2) return en ? "just now" : "à l'instant";
  if (m < 60) return en ? `${m} min ago` : `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return en ? `${h} h ago` : `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? (en ? "yesterday" : "hier") : (en ? `${d} days ago` : `il y a ${d} jours`);
};

export const TIMEZONES = ["Europe/Paris", "Europe/London", "Europe/Madrid", "Europe/Zurich", "Africa/Casablanca", "Africa/Dakar", "America/New_York", "America/Sao_Paulo", "Asia/Dubai", "Asia/Tokyo", "Australia/Sydney"];
